import React, { RefObject, forwardRef, useEffect, useRef, useState } from "react";
import { TTimeOptions, setClass } from "../../utils";
import { CustomPopup } from "./CustomPopup";

interface SelectOptionProps<T, > {
  disabled: boolean;
  isMenuOpen: boolean;
  filteredOptions: T[];
  value: T[] | T | null;
  placeholderNoFiltered: string;
  customOption: string;
  clearOnSelect: boolean;
  defaultValue: T;
  multiple: boolean;
  customTimeOptions?: string[];
  setMenuOpen: (value: boolean) => void; 
  setSearchValue: (value: string) => void; 
  renderOption: (item: T, isSelected: boolean) => JSX.Element;
  getOptionId: (option: Record<string, any> | string | number) => string | number;
  onChange: (value: T | T[]) => void;
}

export const SelectOptions = forwardRef(<T, > ({ 
  disabled, isMenuOpen, filteredOptions, value, placeholderNoFiltered, customOption,
  clearOnSelect, defaultValue, multiple, setMenuOpen, setSearchValue, customTimeOptions,
  renderOption, getOptionId, onChange
}: SelectOptionProps<T>, ref: RefObject<HTMLDivElement>) => {

  const currentRef = useRef<HTMLDivElement>();
  const [isCustomWindow, setCustomWindow] = useState(false);
  const [optionCoords, setCoords] = useState(currentRef?.current?.getBoundingClientRect());
  const [isReverseSelect, setReverseSelect] = useState(false);
  const onCustomSwitch = () => setCustomWindow((prev) => !prev);
  const isArr = Array.isArray(value);
  
  const onSelect = (item: T) => {
    if (isArr) {
      let newValue = [...value];
      const filtered = newValue.filter((elem) => getOptionId(elem) !== getOptionId(item));
      if (filtered.length !== newValue.length) {
        newValue = filtered;
      } else newValue.push(item);
      onChange(!newValue.length && defaultValue ? defaultValue : newValue);
    } else {
      onChange(getOptionId(item) === getOptionId(value) ? defaultValue ?? null : item);
      !multiple && setMenuOpen(false);
    }
    if (clearOnSelect) {
      setSearchValue('');
    }
  };

  const onSave = (val: number) => {
    onChange(val as T);
    setMenuOpen(false);
    onCustomSwitch();
  }

  /**
   * active-option - выставляется если в value стоит тот же опшен 
   * или если это массив(multiple селект), то ищем в списке value объект
  */
  const getOptionClassName = (item: T) =>
  (getOptionId(value) === getOptionId(item)
    || (isArr && value.find(val => getOptionId(val) === getOptionId(item)))
  ) && 'active-option';

  const isSelected = (item: T) => getOptionId(value) === getOptionId(item)
  || (isArr && value.some(val => getOptionId(val) === getOptionId(item)));
  let selectCoords = ref?.current?.getBoundingClientRect();

  useEffect(() => {
    setCoords(currentRef?.current?.getBoundingClientRect());
  }, [isMenuOpen]);

  useEffect(() => {
    if (optionCoords?.height + selectCoords?.bottom + 10 > window.innerHeight) setReverseSelect(true);
    else setReverseSelect(false)
  }, [optionCoords])
  

  return (
    !disabled && isMenuOpen && (
      <div 
        ref={currentRef}
        className={setClass('drop-wrapper', !isCustomWindow && 'overflows')}
        style={{ bottom: isReverseSelect ? selectCoords.height + 4 : null}}
      >
        {isCustomWindow 
          ? <CustomPopup
            onSave={onSave} 
            onCancel={onCustomSwitch} 
            timeOptions={customTimeOptions}
          />
          : (!!filteredOptions.length
            ? (<ul>
                {filteredOptions.map((item) => (
                  <li
                    key={getOptionId(item)}
                    onClick={() => onSelect(item)}
                    className={setClass(getOptionClassName(item))}
                  >
                    {renderOption(item, isSelected(item))}
                  </li>
                ))}
                {customOption && (
                  <li
                    className='custom-option'
                    onClick={onCustomSwitch}
                  >
                    {customOption}
                  </li>
                )}
              </ul>
            ) 
            : <span className="placeholder">{placeholderNoFiltered}</span>
          )
        }
      </div>
    )
  )
})
